import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  BarChart3,
  CalendarDays,
  Cloud,
  Download,
  RefreshCw,
  Users,
} from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import axios from "../api/axios";

const DEFAULT_TARGETS = [
  {
    screen: "landing_page:loa_landing",
    label: "KH quan tâm vào miniapp",
    event_type: "miniapp_landing_page_view",
    actual_screen: "landing_page",
    action: "loa_landing",
    payload_event: "LOA_LANDING",
  },
  {
    screen: "workspace_list",
    label: "Danh sách cửa hàng",
    event_type: "miniapp_workspace_list_view",
    actual_screen: "workspace_list",
    action: "view",
  },
  {
    screen: "dashboard",
    label: "Dashboard",
    event_type: "miniapp_dashboard_view",
    actual_screen: "dashboard",
    action: "view",
  },
  {
    screen: "transaction_list",
    label: "Danh sách giao dịch",
    event_type: "miniapp_transaction_list_view",
    actual_screen: "transaction_list",
    action: "view",
  },

  {
    screen: "landing_page:loa_type",
    label: "KH vào luồng mua/tặng",
    event_type: "miniapp_landing_page_view",
    actual_screen: "landing_page",
    action: "loa_type",
    payload_event: "LOA_TYPE",
  },
  {
    screen: "landing_page:loa_pay_ref",
    label: "Done mua ở miniapp",
    event_type: "miniapp_landing_page_view",
    actual_screen: "landing_page",
    action: "loa_pay_ref",
    payload_event: "LOA_PAY_REF",
  },
  {
    screen: "landing_page:loa_free_ref",
    label: "Done tặng ở miniapp",
    event_type: "miniapp_landing_page_view",
    actual_screen: "landing_page",
    action: "loa_free_ref",
    payload_event: "LOA_FREE_REF",
  },
];

const ENVIRONMENTS = [
  { value: "prod", label: "Production" },
  { value: "dev", label: "Development" },
  { value: "unknown", label: "Unknown" },
];

const CHART_COLORS = [
  "#4f46e5",
  "#0f766e",
  "#f97316",
  "#7c3aed",
  "#dc2626",
  "#0284c7",
  "#65a30d",
];

const METRICS = [
  { value: "unique_users", label: "Unique users" },
  { value: "event_count", label: "Số event" },
];

const formatDateInput = (value) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const todayInput = () => formatDateInput(new Date());
const current30mBucketSlot = () => {
  const now = new Date();
  return now.getHours() * 2 + (now.getMinutes() >= 30 ? 1 : 0);
};

const targetKey = (item) => item.screen;

const targetMeta = (item) => {
  const screen = item.actual_screen || item.screen;
  const action = item.action || "view";
  return `${screen} / ${action}`;
};

const hexToRgb = (hex) => {
  const value = hex.replace("#", "");
  const parsed = parseInt(value, 16);
  return {
    r: (parsed >> 16) & 255,
    g: (parsed >> 8) & 255,
    b: parsed & 255,
  };
};

const heatCellStyle = (targetIndex, value, maxValue) => {
  if (!value || !maxValue) return {};

  const color = CHART_COLORS[targetIndex % CHART_COLORS.length];
  const { r, g, b } = hexToRgb(color);
  const ratio = Math.min(1, Math.max(0.08, value / maxValue));
  const opacity = 0.12 + ratio * 0.76;

  return {
    backgroundColor: `rgba(${r}, ${g}, ${b}, ${opacity})`,
    color: ratio >= 0.48 ? "#ffffff" : "#0f172a",
  };
};

const chartTooltipStyle = {
  borderRadius: 8,
  border: "1px solid #e2e8f0",
  boxShadow: "0 12px 28px rgba(15, 23, 42, 0.12)",
  fontSize: 12,
};

const LINE_CHART_GROUPS = [
  {
    key: "purchase_flow",
    title: "Luồng mua/tặng",
    subtitle: "KH quan tâm, vào luồng mua/tặng, Done mua, Done tặng",
    targetKeys: [
      "landing_page:loa_landing",
      "landing_page:loa_type",
      "landing_page:loa_pay_ref",
      "landing_page:loa_free_ref",
    ],
  },
  {
    key: "feature_flow",
    title: "Luồng chức năng",
    subtitle: "Danh sách cửa hàng, Dashboard, Danh sách giao dịch",
    targetKeys: ["workspace_list", "dashboard", "transaction_list"],
  },
];
function TrackingLineChartCard({ title, subtitle, targets, data }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
      <div className="mb-3">
        <div className="text-sm font-semibold text-slate-800">{title}</div>
        <div className="mt-1 text-xs text-slate-500">{subtitle}</div>
      </div>
      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 6, right: 12, left: 0, bottom: 4 }}
          >
            <CartesianGrid stroke="#d9dee8" strokeDasharray="3 3" />
            <XAxis
              dataKey="time"
              minTickGap={20}
              tickLine={false}
              axisLine={{ stroke: "#d9dee8" }}
              tick={{ fontSize: 12, fill: "#475569" }}
            />
            <YAxis
              allowDecimals={false}
              width={48}
              tickLine={false}
              axisLine={{ stroke: "#d9dee8" }}
              tick={{ fontSize: 12, fill: "#475569" }}
            />
            <Tooltip
              contentStyle={chartTooltipStyle}
              labelStyle={{ color: "#0f172a", fontWeight: 600 }}
            />
            {targets.map((target) => (
              <Line
                key={target.screen}
                type="monotone"
                dataKey={target.screen}
                name={target.label}
                stroke={CHART_COLORS[target.colorIndex % CHART_COLORS.length]}
                strokeOpacity={0.62}
                strokeWidth={1.5}
                dot={false}
                activeDot={{ r: 3 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs">
        {targets.map((target) => {
          const color = CHART_COLORS[target.colorIndex % CHART_COLORS.length];
          return (
            <div key={target.screen} className="inline-flex items-center gap-1.5">
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: color }}
              />
              <span style={{ color }}>{target.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function UserTracking() {
  const [environment, setEnvironment] = useState("prod");
  const [mode, setMode] = useState("day");
  const [day, setDay] = useState(todayInput());
  const [startDay, setStartDay] = useState(todayInput());
  const [endDay, setEndDay] = useState(todayInput());
  const [targets, setTargets] = useState(DEFAULT_TARGETS);
  const [selectedTargets, setSelectedTargets] = useState(
    DEFAULT_TARGETS.map((item) => targetKey(item)),
  );
  const [summary, setSummary] = useState(null);
  const [timeChart, setTimeChart] = useState(null);
  const [chartMetric, setChartMetric] = useState("unique_users");
  const [usersData, setUsersData] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingChart, setLoadingChart] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [health, setHealth] = useState("checking");
  const [errorMsg, setErrorMsg] = useState("");
  const hasFetchedInitialData = useRef(false);

  const selectedPayload = useMemo(
    () => (selectedTargets.length === targets.length ? null : selectedTargets),
    [selectedTargets, targets.length],
  );

  const targetLabelByKey = useMemo(() => {
    return targets.reduce((acc, item) => {
      acc[targetKey(item)] = item.label;
      return acc;
    }, {});
  }, [targets]);

  const chartTargets = useMemo(
    () => (timeChart?.targets?.length ? timeChart.targets : []),
    [timeChart],
  );
  const lineChartGroups = useMemo(() => {
    const targetByKey = chartTargets.reduce((acc, target, index) => {
      acc[target.screen] = {
        ...target,
        colorIndex: index,
      };
      return acc;
    }, {});

    return LINE_CHART_GROUPS.map((group) => ({
      ...group,
      targets: group.targetKeys.map((key) => targetByKey[key]).filter(Boolean),
    })).filter((group) => group.targets.length > 0);
  }, [chartTargets]);
  const chartRows = useMemo(() => {
    const rows = (timeChart?.buckets || []).map((bucket) => ({
      time: bucket.time,
      ...bucket.values,
    }));
    const chartDay = timeChart?.day || day;
    const today = todayInput();

    if (!chartDay || chartDay < today) {
      return rows;
    }
    if (chartDay > today) {
      return [];
    }

    return rows.filter((_, index) => index <= current30mBucketSlot());
  }, [timeChart, day]);
  const chartMaxValue = useMemo(() => {
    let maxValue = 0;
    for (const row of chartRows) {
      for (const target of chartTargets) {
        maxValue = Math.max(maxValue, Number(row[target.screen] || 0));
      }
    }
    return maxValue;
  }, [chartRows, chartTargets]);
  useEffect(() => {
    axios
      .get("/user-tracking/screens")
      .then((res) => {
        const items = res.data?.length ? res.data : DEFAULT_TARGETS;
        setTargets(items);
        setSelectedTargets(items.map((item) => targetKey(item)));
      })
      .catch(() => {
        setTargets(DEFAULT_TARGETS);
        setSelectedTargets(DEFAULT_TARGETS.map((item) => targetKey(item)));
      });

    axios
      .get("/user-tracking/health")
      .then(() => setHealth("ok"))
      .catch(() => setHealth("error"));
  }, []);

  const resetResult = () => {
    setSummary(null);
    setTimeChart(null);
    setUsersData(null);
    setPage(1);
  };

  const buildSummaryPayload = () => {
    if (mode === "day") {
      if (!day) return null;
      return {
        url: "/user-tracking/summary/day",
        body: { environment, day, screens: selectedPayload },
      };
    }

    if (!startDay || !endDay || endDay < startDay) return null;
    return {
      url: "/user-tracking/summary/range",
      body: {
        environment,
        start_day: startDay,
        end_day: endDay,
        screens: selectedPayload,
      },
    };
  };

  const buildUsersPayload = () => {
    if (mode === "day") {
      if (!day) return null;
      return {
        url: "/user-tracking/users/day",
        body: {
          environment,
          day,
          screens: selectedPayload,
          page,
          page_size: pageSize,
        },
      };
    }

    if (!startDay || !endDay || endDay < startDay) return null;
    return {
      url: "/user-tracking/users/range",
      body: {
        environment,
        start_day: startDay,
        end_day: endDay,
        screens: selectedPayload,
        page,
        page_size: pageSize,
      },
    };
  };

  const buildChartPayload = (metric = chartMetric) => {
    if (!day) return null;
    return {
      environment,
      day,
      screens: selectedPayload,
      metric,
    };
  };

  const fetchTimeChart = async (metric = chartMetric) => {
    const payload = buildChartPayload(metric);
    if (!payload) return;

    try {
      setLoadingChart(true);
      const res = await axios.post("/user-tracking/charts/day-time", payload);
      setTimeChart(res.data);
    } catch (err) {
      setErrorMsg(
        err.response?.data?.detail || "Không tải được biểu đồ theo thời gian.",
      );
    } finally {
      setLoadingChart(false);
    }
  };

  const fetchSummary = async () => {
    setErrorMsg("");
    const payload = buildSummaryPayload();
    if (!payload) {
      setErrorMsg("Vui lòng chọn ngày hợp lệ.");
      return;
    }

    try {
      setLoadingSummary(true);
      resetResult();
      const res = await axios.post(payload.url, payload.body);
      setSummary(res.data);
      if (mode === "day") {
        await fetchTimeChart();
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || "Không tải được thống kê.");
    } finally {
      setLoadingSummary(false);
    }
  };

  useEffect(() => {
    if (hasFetchedInitialData.current) return;
    hasFetchedInitialData.current = true;
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMetricChange = async (value) => {
    setChartMetric(value);
    if (summary && mode === "day") {
      await fetchTimeChart(value);
    }
  };

  const fetchUsers = async () => {
    setErrorMsg("");
    const payload = buildUsersPayload();
    if (!payload) {
      setErrorMsg("Vui lòng chọn ngày hợp lệ.");
      return;
    }

    try {
      setLoadingUsers(true);
      const res = await axios.post(payload.url, payload.body);
      setUsersData(res.data);
    } catch (err) {
      setErrorMsg(
        err.response?.data?.detail || "Không tải được danh sách user.",
      );
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (usersData) fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const toggleTarget = (key) => {
    setSelectedTargets((current) => {
      resetResult();
      if (current.includes(key)) {
        return current.filter((item) => item !== key);
      }
      return [...current, key];
    });
  };

  const selectAllTargets = () => {
    setSelectedTargets(targets.map((item) => targetKey(item)));
    resetResult();
  };

  const totalPages = usersData
    ? Math.max(1, Math.ceil((usersData.total || 0) / usersData.page_size))
    : 1;

  const summaryItems =
    summary?.screens ||
    targets.map((item) => ({
      ...item,
      unique_users: null,
    }));

  const totalLoadedUsers = usersData?.total || 0;
  const currentStart = totalLoadedUsers ? (page - 1) * pageSize + 1 : 0;
  const currentEnd = usersData
    ? Math.min(page * pageSize, totalLoadedUsers)
    : 0;

  return (
    <div className="min-h-screen bg-[#f7f8fb] text-slate-800 px-3 py-4 sm:px-4 lg:px-6 lg:py-5">
      <header className="flex flex-wrap items-center gap-3 mb-4 sm:mb-5">
        <h1 className="text-base sm:text-lg font-semibold text-slate-900">
          Thống kê Mini App
        </h1>
        <Cloud
          size={18}
          className={health === "ok" ? "text-emerald-500" : "text-rose-500"}
        />
      </header>

      <section className="bg-white border border-slate-200 rounded-lg px-3 py-4 sm:px-5 mb-5 shadow-sm">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-end lg:gap-4">
          <div className="w-full lg:w-auto">
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">
              Môi trường
            </label>
            <select
              value={environment}
              onChange={(event) => {
                setEnvironment(event.target.value);
                resetResult();
              }}
              className="h-10 w-full lg:min-w-[170px] border border-slate-300 rounded-md px-3 text-sm bg-white text-slate-800"
            >
              {ENVIRONMENTS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          <div className="w-full lg:w-auto">
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">
              Thời gian
            </label>
            <div className="grid grid-cols-2 h-10 border border-slate-300 rounded-md overflow-hidden bg-white lg:inline-grid">
              <button
                type="button"
                onClick={() => {
                  setMode("day");
                  resetResult();
                }}
                className={`px-3 sm:px-4 text-sm ${
                  mode === "day"
                    ? "bg-indigo-600 text-white"
                    : "bg-white text-slate-700"
                }`}
              >
                Theo ngày
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode("range");
                  resetResult();
                }}
                className={`px-3 sm:px-4 text-sm border-l border-slate-300 ${
                  mode === "range"
                    ? "bg-indigo-600 text-white"
                    : "bg-white text-slate-700"
                }`}
              >
                Khoảng ngày
              </button>
            </div>
          </div>

          {mode === "day" ? (
            <div className="w-full lg:w-auto">
              <label className="block text-xs uppercase font-semibold text-transparent mb-1">
                Ngày
              </label>
              <div className="flex items-center gap-2 h-10 w-full border border-slate-300 rounded-md px-3 bg-white">
                <CalendarDays size={16} className="text-slate-500" />
                <input
                  type="date"
                  value={day}
                  onChange={(event) => {
                    setDay(event.target.value);
                    resetResult();
                  }}
                  className="w-full text-sm outline-none bg-transparent"
                />
              </div>
            </div>
          ) : (
            <>
              <div className="w-full lg:w-auto">
                <label className="block text-xs uppercase font-semibold text-transparent mb-1">
                  Từ ngày
                </label>
                <input
                  type="date"
                  value={startDay}
                  onChange={(event) => {
                    setStartDay(event.target.value);
                    resetResult();
                  }}
                  className="h-10 w-full border border-slate-300 rounded-md px-3 text-sm bg-white"
                />
              </div>
              <div className="w-full lg:w-auto">
                <label className="block text-xs uppercase font-semibold text-transparent mb-1">
                  Đến ngày
                </label>
                <input
                  type="date"
                  value={endDay}
                  onChange={(event) => {
                    setEndDay(event.target.value);
                    resetResult();
                  }}
                  className="h-10 w-full border border-slate-300 rounded-md px-3 text-sm bg-white"
                />
              </div>
            </>
          )}

          <button
            type="button"
            onClick={fetchSummary}
            disabled={loadingSummary || selectedTargets.length === 0}
            className="inline-flex w-full items-center justify-center gap-2 h-10 px-5 rounded-md bg-indigo-600 text-white text-sm font-semibold disabled:opacity-60 sm:col-span-2 lg:ml-auto lg:w-auto lg:col-span-1"
          >
            {loadingSummary ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <BarChart3 size={16} />
            )}
            Xem thống kê
          </button>
        </div>

        <div className="mt-5 border-t border-slate-100 pt-4">
          <div className="flex flex-col gap-2 mb-2 sm:flex-row sm:items-center sm:justify-between">
            <label className="text-xs font-semibold text-slate-600">
              Bộ lọc Màn hình / Action cần thống kê
            </label>
            <button
              type="button"
              onClick={selectAllTargets}
              className="text-xs text-indigo-600 font-medium"
            >
              Chọn tất cả
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {targets.map((item) => {
              const key = targetKey(item);
              const checked = selectedTargets.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleTarget(key)}
                  title={targetMeta(item)}
                  className={`inline-flex min-h-8 max-w-full items-center gap-2 rounded-full border px-3 py-1.5 text-left text-xs leading-snug transition sm:h-8 sm:py-0 ${
                    checked
                      ? "border-indigo-500 text-indigo-700 bg-indigo-50"
                      : "border-slate-300 text-slate-600 bg-white"
                  }`}
                >
                  <span
                    className={`h-4 w-4 rounded border flex items-center justify-center ${
                      checked
                        ? "border-indigo-500 bg-indigo-500"
                        : "border-slate-300 bg-white"
                    }`}
                  >
                    {checked && (
                      <span className="h-1.5 w-1.5 rounded-full bg-white" />
                    )}
                  </span>
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {errorMsg && (
        <div className="mb-4 border border-red-200 bg-red-50 text-red-700 px-4 py-3 rounded-md text-sm">
          {errorMsg}
        </div>
      )}

      <section className="grid grid-cols-1 gap-3 mb-5 sm:grid-cols-2 lg:gap-4 xl:grid-cols-4">
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg p-4 text-white shadow-sm sm:p-5">
          <div className="flex items-center gap-2 text-sm font-semibold opacity-95">
            <Users size={18} />
            Tổng unique user
          </div>
          <div className="mt-5 text-3xl font-bold sm:mt-6 sm:text-4xl">
            {summary?.total_unique_users ?? "--"}
          </div>
        </div>
        {summaryItems.map((item) => (
          <div
            key={item.screen}
            className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm sm:p-5"
          >
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <BarChart3 size={16} className="text-slate-500" />
              {item.label}
            </div>
            <div className="text-xs text-slate-400 mt-1">
              {item.actual_screen || item.screen} / {item.action || "view"}
            </div>
            <div className="mt-3 text-2xl font-bold text-slate-900 sm:mt-4 sm:text-3xl">
              {item.unique_users ?? "--"}
            </div>
          </div>
        ))}
      </section>

      <section className="mb-5 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">
              Biểu đồ theo thời gian trong ngày
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Theo dõi user thường vào app ở khung giờ nào và vào màn/action
              nào.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <select
              value={chartMetric}
              onChange={(event) => handleMetricChange(event.target.value)}
              disabled={mode !== "day" || loadingChart}
              className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 shadow-sm sm:w-auto"
            >
              {METRICS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => fetchTimeChart()}
              disabled={
                mode !== "day" || loadingChart || selectedTargets.length === 0
              }
              className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60 sm:w-auto"
            >
              {loadingChart ? (
                <RefreshCw size={15} className="animate-spin" />
              ) : (
                <BarChart3 size={15} />
              )}
              Tải biểu đồ
            </button>
          </div>
        </div>

        {mode !== "day" ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
            Biểu đồ theo khung giờ chỉ áp dụng cho chế độ theo ngày.
          </div>
        ) : !timeChart ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
            Chọn bộ lọc rồi bấm Xem thống kê hoặc Tải biểu đồ.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {lineChartGroups.map((group) => (
                <TrackingLineChartCard
                  key={group.key}
                  title={group.title}
                  subtitle={group.subtitle}
                  targets={group.targets}
                  data={chartRows}
                />
              ))}
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm font-semibold text-slate-800">
                  Heatmap khung giờ nóng
                </div>
                <div className="inline-flex self-start rounded bg-indigo-600 px-2 py-1 text-xs font-medium text-white sm:self-auto">
                  Màu càng đậm nghĩa là giá trị càng cao.
                </div>
              </div>
              <div className="overflow-x-auto pb-2">
                <div className="min-w-[1100px] space-y-2">
                  <div className="grid grid-cols-[170px_repeat(48,minmax(24px,1fr))] gap-1 text-[10px] text-slate-400">
                    <div />
                    {chartRows.map((row) => (
                      <div key={row.time} className="text-center">
                        {row.time.endsWith(":00") ? row.time : ""}
                      </div>
                    ))}
                  </div>
                  {chartTargets.map((target, targetIndex) => (
                    <div
                      key={target.screen}
                      className="grid grid-cols-[170px_repeat(48,minmax(24px,1fr))] items-center gap-1"
                    >
                      <div className="truncate pr-2 text-xs font-medium text-slate-700">
                        {target.label}
                      </div>
                      {chartRows.map((row) => {
                        const value = Number(row[target.screen] || 0);
                        return (
                          <div
                            key={`${target.screen}-${row.time}`}
                            title={`${target.label} - ${row.time}: ${value}`}
                            className={`flex h-7 items-center justify-center rounded text-[10px] font-medium ${
                              value ? "" : "bg-slate-50 text-transparent"
                            }`}
                            style={heatCellStyle(
                              targetIndex,
                              value,
                              chartMaxValue,
                            )}
                          >
                            {value || ""}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
      <section className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="flex flex-col items-stretch gap-3 px-3 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Danh sách User
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {usersData
                ? `Tổng cộng ${usersData.total} user theo bộ lọc hiện tại`
                : "Chưa tải dữ liệu"}
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <label className="text-sm text-slate-500">Hiển thị:</label>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
              className="h-9 w-full border border-slate-300 rounded-md px-2 text-sm bg-white sm:w-auto"
            >
              {[25, 50, 100, 200].map((value) => (
                <option key={value} value={value}>
                  {value} / trang
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={fetchUsers}
              disabled={loadingUsers || !summary}
              className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-4 text-sm text-slate-700 disabled:opacity-60 sm:w-auto"
            >
              {loadingUsers ? (
                <RefreshCw size={15} className="animate-spin" />
              ) : (
                <Download size={15} />
              )}
              Tải danh sách
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[640px] text-sm">
            <thead>
              <tr className="border-y bg-slate-50 text-left text-slate-500 uppercase text-xs">
                <th className="px-3 py-3 font-semibold sm:px-5">User ID</th>
                <th className="px-3 py-3 font-semibold sm:px-5">
                  Màn / action đã vào
                </th>
              </tr>
            </thead>
            <tbody>
              {(usersData?.users || []).map((item) => (
                <tr
                  key={item.user_id}
                  className="border-b border-slate-100 last:border-0"
                >
                  <td className="px-3 py-4 font-semibold text-slate-900 sm:px-5">
                    {item.user_id}
                  </td>
                  <td className="px-3 py-4 text-slate-700 sm:px-5">
                    <div className="flex flex-wrap gap-2">
                      {item.screens.map((screen) => (
                        <span
                          key={screen}
                          className="inline-flex items-center rounded bg-slate-100 px-2 py-1 text-xs text-slate-700"
                        >
                          {targetLabelByKey[screen] || screen}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
              {!usersData?.users?.length && (
                <tr>
                  <td
                    colSpan={2}
                    className="px-3 py-10 text-center text-slate-500 sm:px-5"
                  >
                    Không có dữ liệu
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 px-3 py-4 text-sm sm:px-5 md:flex-row md:items-center md:justify-between">
          <span className="text-slate-500">
            {usersData
              ? `Hiển thị ${currentStart} đến ${currentEnd} trong số ${totalLoadedUsers} kết quả`
              : "Chưa có dữ liệu"}
          </span>
          <div className="inline-flex self-start border border-slate-300 rounded-md overflow-hidden md:self-auto">
            <button
              type="button"
              disabled={page <= 1 || loadingUsers}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              className="px-3 py-2 disabled:opacity-50 bg-white"
            >
              Trước
            </button>
            {Array.from(
              { length: Math.min(3, totalPages) },
              (_, index) => index + 1,
            ).map((value) => (
              <button
                key={value}
                type="button"
                disabled={loadingUsers}
                onClick={() => setPage(value)}
                className={`px-3 py-2 border-l border-slate-300 ${
                  page === value ? "bg-indigo-50 text-indigo-700" : "bg-white"
                }`}
              >
                {value}
              </button>
            ))}
            <button
              type="button"
              disabled={page >= totalPages || loadingUsers}
              onClick={() => setPage((value) => value + 1)}
              className="px-3 py-2 border-l border-slate-300 disabled:opacity-50 bg-white"
            >
              Sau
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
